import React, { Component } from 'react'
import { Product as ProductPb, ProductStatus, ChannelType, Shipping, Restriction } from '../../rpc/admin/products_pb';
import { Image as ImagePb } from '../../rpc/admin/image_pb';
import Input from '../forms/input';
import feather from '../../img/feather-sprite.svg';
import Select from '../forms/select';
import { ProductContext } from './context';
import DecimalInput from '../forms/decimal_input';
import Attributes from './attributes';
import { Category } from '../../rpc/admin/categories_pb';
import Suppliers from './suppliers';
import Images from './images';
import SelectList from '../forms/select_list';

export interface VariantValidation {
    styleError?: string;
    slugError?: string;
    priceError?: string;
}

interface VariantProps {
    open?: boolean;
    variant?: ProductPb.Variant;
    category?: Category;
    editing?: boolean;
    index?: number;
    count?: number;
    validation?: VariantValidation,
    onChange?: (index: number, variant: ProductPb.Variant) => void;
    onToggleOpen?: (index: number) => void;
    onPromote?: (index: number) => void;
    onDemote?: (index: number) => void;
    onDelete?: (index: number) => void;
    onCopy?: (index: number) => void;
}

interface VariantState {}

class Variant extends Component<VariantProps, VariantState> {

    constructor(props: VariantProps) {
        super(props);
        this.state = {};
    }

    private handleInputChange = (value: string, name?: string) => {
        const variant = this.props.variant!.clone();
        switch (name) {
            case 'style':
                variant.setStyle(value);
                break;
            case 'slug':
                variant.setSlug(value);
                break;
            case 'mpn':
                variant.setManufacturerPartNumber(value);
                break;
            case 'barcode':
                variant.setBarcode(value);
                break;
        }
        if (this.props.onChange) {
            this.props.onChange(this.props.index!, variant);
        }
    }

    private handleDecimalInputChange = (value: number, name?: string) => {
        const variant = this.props.variant!.clone();
        let channel: ProductPb.Variant.Channel;
        switch (name) {
            case 'width':
                variant.setWidthInches(value);
                break;
            case 'height':
                variant.setHeightInches(value);
                break;
            case 'length':
                variant.setLengthInches(value);
                break;
            case 'weight':
                variant.setWeightOunces(value);
                break;
            case 'price':
                if (variant.getChannelsList().length) {
                    channel = variant.getChannelsList()[0];
                } else {
                    channel = new ProductPb.Variant.Channel();
                    channel.setChannelType(ChannelType.HAWK_AMERICAN);
                }
                channel.setPrice(value);
                variant.setChannelsList([channel]);
                break;
            case 'msrp':
                variant.setMsrp(value);
                break;
            case 'mapp':
                variant.setMapp(value);
                break;
            case 'compare-at':
                variant.setCompareAt(value);
                break;
        }
        if (this.props.onChange) {
            this.props.onChange(this.props.index!, variant);
        }
    }

    private handleOpenClick = (e: React.FormEvent<HTMLButtonElement>) => {
        if (this.props.onToggleOpen) {
            this.props.onToggleOpen(this.props.index!);
        }
    }

    private handlePromote = (e: React.FormEvent<HTMLButtonElement>) => {
        if (this.props.onPromote) {
            this.props.onPromote(this.props.index!);
        }
    }

    private handleDemote = (e: React.FormEvent<HTMLButtonElement>) => {
        if (this.props.onDemote) {
            this.props.onDemote(this.props.index!);
        }
    }

    private handleCopy = (e: React.FormEvent<HTMLButtonElement>) => {
        if (this.props.onCopy) {
            this.props.onCopy(this.props.index!);
        }
    }

    private handleDelete = (e: React.FormEvent<HTMLButtonElement>) => {
        if (this.props.onDelete) {
            this.props.onDelete(this.props.index!);
        }
    }

    private handleOnStatusChange = (p: ProductStatus) => {
        const variant = this.props.variant!.clone();
        let channel: ProductPb.Variant.Channel;
        if (variant.getChannelsList().length) {
            channel = variant.getChannelsList()[0];
        } else {
            channel = new ProductPb.Variant.Channel();
            channel.setChannelType(ChannelType.HAWK_AMERICAN);
        }
        channel.setStatus(p);
        variant.setChannelsList([channel]);
        if (this.props.onChange) {
            this.props.onChange(this.props.index!, variant);
        }
    }

    private handleChangeRestrictions = (values: Array<Restriction>) => {
        const variant = this.props.variant!.clone();
        variant.setRestrictionsList(values);
        if (this.props.onChange) {
            this.props.onChange(this.props.index!, variant);
        }
    }

    private handleChangeShipping = (values: Array<Shipping>) => {
        const variant = this.props.variant!.clone();
        variant.setShippingList(values);
        if (this.props.onChange) {
            this.props.onChange(this.props.index!, variant);
        }
    }

    private handleChangeAttributes = (values: Array<ProductPb.Variant.AttributeValuePair>) => {
        const variant = this.props.variant!.clone();
        variant.setAttributesList(values);
        if (this.props.onChange) {
            this.props.onChange(this.props.index!, variant);
        }
    }

    private handleChangeSuppliers = (values: Array<ProductPb.Variant.SupplierInventory>) => {
        const variant = this.props.variant!.clone();
        variant.setSupplierInventoriesList(values);
        if (this.props.onChange) {
            this.props.onChange(this.props.index!, variant);
        }
    }

    private handleChangeImages = (values: Array<ImagePb>) => {
        const variant = this.props.variant!.clone();
        variant.setImagesList(values);
        if (this.props.onChange) {
            this.props.onChange(this.props.index!, variant);
        }
    }

    render() {
        const variant = this.props.variant!;
        let channel: ProductPb.Variant.Channel;
        if (variant.getChannelsList().length) {
            channel = variant.getChannelsList()[0];
        } else {
            channel = new ProductPb.Variant.Channel();
            channel.setChannelType(ChannelType.HAWK_AMERICAN);
            channel.setPrice(0);
        }

        return (
            <ProductContext.Consumer>
                {ctx => (
                <>
                    <div className="is-light variant-header" role="navigation" aria-label={"variant" + (this.props.index! + 1)}>
                        <div className="variant-header-start">
                            <button className="button is-small" title="Promote" onClick={this.handleOpenClick}>
                                <svg width="16" height="16" className={"button-icon" + (this.props.open ? "" : " variant-closed")}>
                                    <use href={feather + "#chevron-down"}/>
                                </svg>
                            </button>
                        </div>
                        
                        <div className="variant-header-label"><span>{variant.getStyle()}</span></div>

                        <div className="variant-header-end">
                            <button className="button is-small" title="Promote" disabled={!this.props.index} onClick={this.handlePromote}>
                                <svg width="16" height="16" className="button-icon">
                                    <use href={feather + "#arrow-up"}/>
                                </svg>
                            </button>
                            <button className="button is-small" title="Demote" disabled={this.props.index === this.props.count! - 1} onClick={this.handleDemote}>
                                <svg width="16" height="16" className="button-icon">
                                    <use href={feather + "#arrow-down"}/>
                                </svg>
                            </button>
                            <button className="button is-small" title="Copy" onClick={this.handleCopy}>
                                <svg width="16" height="16" className="button-icon">
                                    <use href={feather + "#copy"}/>
                                </svg>
                            </button>
                            <button className="button is-small" title="Delete" disabled={this.props.count! === 1} onClick={this.handleDelete}>
                                <svg width="16" height="16" className="button-icon">
                                    <use href={feather + "#trash"}/>
                                </svg>
                            </button>
                        </div>
                    </div>

                    { this.props.open &&
                        <>
                            <div className="columns">
                                <div className="column"><Input label="Style"
                                    name="style"
                                    disabled={!this.props.editing}
                                    errorMessage={this.props.validation?.styleError}
                                    value={variant.getStyle()}
                                    onChange={this.handleInputChange} />
                                </div>
                                <div className="column"><Select<ProductStatus> label="Status"
                                    disabled={!this.props.editing}
                                    value={channel.getStatus()}
                                    options={ctx.statusOptions}
                                    onChange={this.handleOnStatusChange} />
                                </div>
                                <div className="column"><Input label="SKU"
                                    name="sku"
                                    disabled={true}
                                    value={variant.getSku().toString()} />
                                </div>
                            </div>

                            <div className="columns">
                                <div className="column"><Input label="Slug"
                                    name="slug"
                                    disabled={!this.props.editing}
                                    errorMessage={this.props.validation?.slugError}
                                    value={variant.getSlug()}
                                    onChange={this.handleInputChange} />
                                </div>
                                <div className="column"><Input label="Manufacturer Part Number"
                                    name="mpn"
                                    disabled={!this.props.editing}
                                    value={variant.getManufacturerPartNumber()}
                                    onChange={this.handleInputChange} />
                                </div>
                                <div className="column"><Input label="UPC"
                                    name="barcode"
                                    disabled={!this.props.editing}
                                    value={variant.getBarcode()}
                                    onChange={this.handleInputChange} />
                                </div>
                            </div>

                            <div className="columns">
                                <div className="column"><DecimalInput label="Width (in)"
                                    name="width"
                                    disabled={!this.props.editing}
                                    value={variant.getWidthInches()}
                                    onChange={this.handleDecimalInputChange} />
                                </div>
                                <div className="column"><DecimalInput label="Height (in)"
                                    name="height"
                                    disabled={!this.props.editing}
                                    value={variant.getHeightInches()}
                                    onChange={this.handleDecimalInputChange} />
                                </div>
                                <div className="column"><DecimalInput label="Length (in)"
                                    name="length"
                                    disabled={!this.props.editing}
                                    value={variant.getLengthInches()}
                                    onChange={this.handleDecimalInputChange} />
                                </div>
                                <div className="column"><DecimalInput label="Weight (oz)"
                                    name="weight"
                                    disabled={!this.props.editing}
                                    value={variant.getWeightOunces()}
                                    onChange={this.handleDecimalInputChange} />
                                </div>
                            </div>

                            <div className="columns">
                                <div className="column"><DecimalInput label="Price"
                                    name="price"
                                    currency={true}
                                    disabled={!this.props.editing}
                                    errorMessage={this.props.validation?.priceError}
                                    value={channel.getPrice()}
                                    onChange={this.handleDecimalInputChange} />
                                </div>
                                <div className="column"><DecimalInput label="MSRP"
                                    name="msrp"
                                    currency={true}
                                    disabled={!this.props.editing}
                                    value={variant.getMsrp()}
                                    onChange={this.handleDecimalInputChange} />
                                </div>
                                <div className="column"><DecimalInput label="MAPP"
                                    name="mapp"
                                    currency={true}
                                    disabled={!this.props.editing}
                                    value={variant.getMapp()}
                                    onChange={this.handleDecimalInputChange} />
                                </div>
                                <div className="column"><DecimalInput label="Compare At"
                                    name="compare-at"
                                    currency={true}
                                    disabled={!this.props.editing}
                                    value={variant.getCompareAt()}
                                    onChange={this.handleDecimalInputChange} />
                                </div>
                            </div>

                            <div className="columns">
                                <div className="column"><SelectList<Shipping>
                                    label="Shipping"
                                    disabled={!this.props.editing}
                                    values={variant.getShippingList()}
                                    options={ctx.shippingOptions}
                                    onChange={this.handleChangeShipping} />
                                </div>
                                <div className="column"><SelectList<Restriction>
                                    label="Restrictions"
                                    disabled={!this.props.editing}
                                    values={variant.getRestrictionsList()}
                                    options={ctx.restrictionOptions}
                                    onChange={this.handleChangeRestrictions} />
                                </div>
                            </div>

                            <div className="columns">
                                <div className="column"><Attributes values={variant.getAttributesList()}
                                    category={this.props.category}
                                    onChange={this.handleChangeAttributes} />
                                </div>
                            </div>

                            <div className="columns">
                                <div className="column"><Suppliers values={variant.getSupplierInventoriesList()}
                                    onChange={this.handleChangeSuppliers} />
                                </div>
                            </div>

                            <div className="columns">
                                <div className="column images-column"><Images values={variant.getImagesList()}
                                    onChange={this.handleChangeImages} />
                                </div>
                            </div>
                        </>
                    }
                    </>
                )}
            </ProductContext.Consumer>
        );
    }
}

export default Variant;
